import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { createApi } from 'ReduxHelpers';
import {
    type GetGeoClustersParams,
    type GetGeoClustersResponse,
    type GeoJsonFeatureCollection,
    type Viewport,
} from './studentNetworkV2Api.types';

// Build bracket-notation query string for the filters param,
// matching how the Angular Iguana model serialized this request.
function buildFiltersQueryString(filters: GetGeoClustersParams['filters']): string {
    const parts: string[] = [];

    if (filters.viewports) {
        filters.viewports.forEach((vp: Viewport) => {
            parts.push(`filters[viewports][]=${vp.join(',')}`);
        });
    }

    parts.push(`filters[zoom]=${filters.zoom}`);
    parts.push(`filters[marker_width]=${filters.marker_width}`);

    if (filters.program_type) parts.push(`filters[program_type]=${encodeURIComponent(filters.program_type)}`);
    if (filters.keyword_search) parts.push(`filters[keyword_search]=${encodeURIComponent(filters.keyword_search)}`);
    if (filters.alumni != null) parts.push(`filters[alumni]=${filters.alumni}`);

    return parts.join('&');
}

export const studentNetworkMapsApi = createApi('studentNetworkMapsApi', {
    ...getBackRoyalApiBaseConfig('studentNetworkMapsApi'),
    endpoints: builder => ({
        getGeoClusters: builder.query<GeoJsonFeatureCollection, GetGeoClustersParams>({
            query: ({ filters }) => ({
                url: `student_network_maps.json?${buildFiltersQueryString(filters)}`,
                method: 'GET',
            }),
            // Note: getBackRoyalApiBaseConfig auto-camelCases response keys,
            // so student_network_maps becomes studentNetworkMaps
            transformResponse: (response: GetGeoClustersResponse) => {
                const raw =
                    (response.contents as Record<string, unknown>).studentNetworkMaps ??
                    response.contents.student_network_maps;
                if (!raw) return { type: 'FeatureCollection', features: [] };

                // Response is an array with one FeatureCollection, or a JSON string
                if (Array.isArray(raw)) return raw[0] ?? { type: 'FeatureCollection', features: [] };
                if (typeof raw === 'string') {
                    const parsed = JSON.parse(raw);
                    return Array.isArray(parsed) ? parsed[0] : parsed;
                }
                return raw as GeoJsonFeatureCollection;
            },
        }),
    }),
});

export const { useGetGeoClustersQuery } = studentNetworkMapsApi;
